import React from "react";
import { FileText, HeartHandshake, Sparkles, Target } from "lucide-react";
import Button from "../components/Button";
import { Blob, Star } from "../components/Decor";
import AccentRays from "../components/decor/AccentRays";
import HandDrawnUnderline from "../components/decor/HandDrawnUnderline";
import SEO from "../components/SEO";

const values = [
  {
    title: "Relevan",
    description: "Rekomendasi disesuaikan dengan kebutuhan dan preferensi keluarga.",
    icon: Target,
    iconClassName: "bg-mint/45",
  },
  {
    title: "Informasi lebih jelas",
    description:
      "Informasi penting disusun dalam satu tempat agar lebih mudah dipahami dan dibandingkan.",
    icon: FileText,
    iconClassName: "bg-yellow/55",
  },
  {
    title: "Berpihak pada keluarga",
    description:
      "Dirancang untuk membantu keluarga mengambil keputusan dengan lebih percaya diri.",
    icon: HeartHandshake,
    iconClassName: "bg-coral/45",
  },
];

export default function Tentang() {
  return (
    <div className="relative overflow-hidden bg-cream">
      <SEO
        title="Tentang MySchoolMate"
        description="Kenali MySchoolMate, platform yang membantu keluarga mencari dan membandingkan sekolah dengan lebih mudah."
        canonicalPath="/tentang"
      />
      <section
        aria-labelledby="about-heading"
        className="relative overflow-hidden"
      >
        <Blob
          className="pointer-events-none absolute -left-28 -top-28 h-80 w-80 opacity-20"
          color="#DCD5FF"
        />
        <Blob
          className="pointer-events-none absolute -bottom-36 -right-28 h-96 w-96 opacity-20"
          color="#73DCC6"
        />

        <div className="relative z-10 mx-auto max-w-[1280px] px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1.16fr)_minmax(0,0.94fr)] lg:gap-10 xl:gap-16">
            <div className="mx-auto w-full max-w-[680px] text-center lg:mx-0 lg:text-left">
              <span className="inline-flex items-center gap-2 rounded-pill border border-ink/20 bg-mint/20 px-4 py-2 text-xs font-extrabold uppercase text-ink sm:text-sm">
                <Sparkles aria-hidden="true" className="h-4 w-4 text-purple" />
                Tentang MySchoolMate
              </span>

              <h1
                id="about-heading"
                className="relative mt-6 text-4xl font-extrabold leading-[1.08] tracking-normal sm:text-5xl lg:text-[3rem]"
              >
                <AccentRays
                  variant="curved-rays"
                  color="mint"
                  rotation={12}
                  className="absolute -right-1 -top-8 h-10 w-10 sm:right-3 lg:-right-2 lg:-top-4"
                />
                Membantu keluarga Indonesia memilih sekolah dengan{" "}
                <span className="relative inline-block">
                  lebih tenang
                  <HandDrawnUnderline
                    variant="brush"
                    color="yellow"
                    className="absolute -bottom-2 left-[-3%] h-3 w-[106%]"
                  />
                </span>{"."}
              </h1>

              <div className="mx-auto mt-7 max-w-[610px] space-y-4 text-base font-medium leading-relaxed text-ink/70 sm:text-lg lg:mx-0">
                <p>
                  MySchoolMate lahir dari satu pertanyaan sederhana: kenapa memilih sekolah untuk
                  anak terasa begitu melelahkan?
                </p>
                <p>
                  Kami membantu menyatukan informasi sekolah, memudahkan perbandingan, dan
                  mencocokkan pilihan dengan kebutuhan keluarga.
                </p>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-[580px] lg:mx-0">
              <Blob
                className="pointer-events-none absolute -right-10 -top-10 z-0 h-44 w-44 opacity-45 sm:h-52 sm:w-52"
                color="#FFD966"
              />
              <Blob
                className="pointer-events-none absolute -bottom-12 -left-12 z-0 h-40 w-40 opacity-55"
                color="#73DCC6"
              />
              <Star
                className="pointer-events-none absolute -right-1 top-[18%] z-30 h-8 w-8 sm:-right-4 sm:h-10 sm:w-10"
                color="#6554E8"
              />

              <div className="relative z-10 aspect-[4/3]">
                <div
                  className="absolute inset-0 translate-x-2 translate-y-2 bg-ink sm:translate-x-3 sm:translate-y-3"
                  style={{ borderRadius: "36% 18% 30% 16% / 24% 20% 34% 28%" }}
                  aria-hidden="true"
                />
                <div
                  className="relative h-full overflow-hidden border-2 border-ink bg-cream-2"
                  style={{ borderRadius: "36% 18% 30% 16% / 24% 20% 34% 28%" }}
                >
                  <img
                    src="/assets/landing/3people.png"
                    alt="Keluarga berdiskusi bersama saat mencari pilihan sekolah"
                    width="1672"
                    height="941"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3 lg:mt-14 lg:gap-6">
            {values.map(({ title, description, icon: Icon, iconClassName }) => (
              <article
                key={title}
                className="flex min-h-[234px] flex-col rounded-[24px] border-2 border-ink bg-white p-6 shadow-[0_14px_30px_-24px_rgba(33,26,77,0.45)] sm:p-7"
              >
                <span
                  className={`flex h-[52px] w-[52px] items-center justify-center rounded-[15px] border border-ink/20 ${iconClassName}`}
                  aria-hidden="true"
                >
                  <Icon className="h-6 w-6" strokeWidth={2} />
                </span>
                <h2 className="mt-5 min-h-[30px] text-xl font-extrabold leading-tight sm:text-[1.35rem]">
                  {title}
                </h2>
                <p className="mt-3 text-sm font-medium leading-relaxed text-ink/70 sm:text-[15px]">
                  {description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 pb-4 sm:px-6 lg:px-8 pt-4 lg:pt-8">
        <div className="relative overflow-hidden rounded-[30px] bg-[#E0F7EF] flex items-center border border-ink/15">
          <img 
            src="/assets/landing/cta2.png" 
            alt="" 
            className="absolute inset-0 h-full w-full object-cover sm:object-contain sm:object-right"
          />
          
          <div className="relative z-10 p-6 sm:p-8 lg:px-12 lg:py-10 w-full max-w-3xl">
            <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-extrabold text-ink leading-tight mb-2">
              <span className="block">Langkah kecil hari ini,</span>
              <span className="block">keputusan besar untuk masa depan.</span>
            </h2>
            <p className="text-ink/75 text-base sm:text-[17px] mb-6 max-w-md font-medium leading-relaxed">
              Jelajahi, bandingkan, dan temukan sekolah yang paling sesuai untuk anak Anda.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Button to="/match" size="md" className="!bg-[#1A1341] !text-white w-full sm:w-auto shadow-sm hover:!bg-[#281e60]">
                <Sparkles size={16} className="inline mr-2" /> Mulai AI School Match
              </Button>
              <Button to="/schools" size="md" className="!bg-transparent !text-ink !border !border-ink/20 hover:!bg-white/40 w-full sm:w-auto">
                Jelajahi Sekolah
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
